import { useId, useMemo, useState } from "react";
import { Link } from "wouter";
import { isNotImplemented } from "../../api/errors";
import { useApplyImport, useProposeImport, useProviderCredentials } from "../../api/queries";
import type { ApplyImportResult, ProposedImportItem } from "../../api/types";
import { useAuth } from "../../auth/AuthContext";
import { canManageFleet, canSeeFleet } from "../../auth/roleView";
import { Banner } from "../../components/Banner";
import { Button } from "../../components/Button";
import { DataTable, type DataTableColumn } from "../../components/DataTable";
import { EmptyState, LoadingState } from "../../components/AsyncStates";
import { FormError } from "../../components/FormError";
import { PageHeader } from "../../components/PageHeader";
import { ImportConfidencePill, ImportConflictPill } from "../../components/StatePill";
import { PROVIDER_LABEL } from "../provider-credentials/ProviderCredentialsPage";
import "../../components/Form.css";
import "./org-import.css";

/** A deferred, no-credential source — the value encodes straight onto
 * `ProposeImportParams.provider`; there is no `credentialId` to resolve for either
 * (the server refuses them before it would try, see `deferredImportReasons`). */
const DEFERRED_SOURCES: { provider: "github" | "docker"; label: string }[] = [
  { provider: "github", label: "GitHub org" },
  { provider: "docker", label: "Docker Hub" },
];

const CREDENTIAL_PREFIX = "cred:";
const DEFERRED_PREFIX = "deferred:";

/** Renders `applyImport`'s result as one plain-language sentence (a single text
 * node — easier to assert on and to read than a scattering of JSX expressions). */
function formatApplyResult(result: ApplyImportResult): string {
  const created = `Created ${result.projectsCreated} new project${result.projectsCreated === 1 ? "" : "s"}`;
  const existed = result.projectsExisted > 0 ? ` (${result.projectsExisted} already existed)` : "";
  const skipped = `${result.skipped} item${result.skipped === 1 ? "" : "s"} left unselected or not committable.`;
  return `${created}${existed}. ${skipped}`;
}

/** Stable per-item key: `sourceIds` + `level` + `name` is unique within one proposal
 * (two items never share every one of those), and — unlike an array INDEX — stays
 * attached to the same logical item if a future revision reorders the response. */
function itemKey(item: ProposedImportItem): string {
  return `${item.level}:${item.name}:${item.sourceIds?.join(",") ?? ""}`;
}

/**
 * "Import existing infrastructure" (ADR-0048 §2, #472) — the web half of the
 * infer -> confirm -> apply flow the backend (`server/internal/provisioning/http.go`
 * `ProposeImportHandler`/`ApplyImportHandler`) already exposes. Reads an existing
 * provider organization through a connected provider credential, renders the
 * server's inferred canonical mapping with its per-item confidence/conflict
 * annotations, and lets the operator select which committable items to actually
 * create before anything is written — propose is READ-ONLY and commits nothing on
 * its own (never called as a side effect of picking a source).
 *
 * Serves the Migrator persona's brownfield on-ramp and the idea-stage Founder who
 * already has infra in a provider account (docs/product/personas.md) — reachable
 * from the first-run "connect one you have" step alongside enrollment.
 */
export function OrgImportPage() {
  const { session } = useAuth();
  const sourceFieldId = useId();

  // Mirrors the server's authz matrix exactly (server/internal/authz/model.go):
  // provider_credential:view (propose) is granted to viewer/operator/admin — the
  // same set canSeeFleet already names; project:create (apply) is operator/admin
  // only. This is UX only (S-8) — the server enforces the real decision on every
  // request regardless of what this check says.
  const canPropose = canSeeFleet(session?.role);
  const canApply = canManageFleet(session?.role);

  const { data: credentialsPage, isPending: credentialsPending } = useProviderCredentials();
  const credentials = credentialsPage?.items ?? [];

  const [source, setSource] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const proposeMutation = useProposeImport();
  const applyMutation = useApplyImport();

  const proposal = proposeMutation.data;
  const items = useMemo(() => proposal?.items ?? [], [proposal]);
  const proposeError = proposeMutation.error;
  // Computed once into a plain boolean rather than calling `isNotImplemented` a
  // second time in the negated branch below — TS narrows a repeated call on the
  // SAME expression to `never` in the `!` branch (the positive branch already
  // narrowed it to exactly the guard's type), which then can't read `.message`.
  const proposeDeferred = Boolean(proposeMutation.isError && proposeError && isNotImplemented(proposeError));

  function handlePropose() {
    applyMutation.reset();
    if (source.startsWith(CREDENTIAL_PREFIX)) {
      const credentialId = source.slice(CREDENTIAL_PREFIX.length);
      const credential = credentials.find((c) => c.id === credentialId);
      if (!credential) return;
      proposeMutation.mutate(
        { provider: credential.kind, credentialId },
        {
          onSuccess: (result) => {
            // Every COMMITTABLE item starts selected — "infer, then confirm": the
            // operator deselects what it does NOT want, rather than hunting through
            // the list to opt every safe item back in (ADR-0048 §2).
            setSelected(new Set(result.items.filter((it) => it.committable).map(itemKey)));
          },
        },
      );
    } else if (source.startsWith(DEFERRED_PREFIX)) {
      const provider = source.slice(DEFERRED_PREFIX.length) as "github" | "docker";
      setSelected(new Set());
      proposeMutation.mutate({ provider });
    }
  }

  function toggleSelected(key: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function handleApply() {
    const toApply = items.filter((it) => it.committable && selected.has(itemKey(it)));
    applyMutation.mutate(toApply);
  }

  const committableCount = items.filter((it) => it.committable).length;
  const selectedCommittableCount = items.filter((it) => it.committable && selected.has(itemKey(it))).length;

  const columns: DataTableColumn<ProposedImportItem>[] = [
    {
      key: "select",
      header: <span className="visually-hidden">Import this item</span>,
      cell: (it) =>
        it.committable ? (
          <input
            type="checkbox"
            aria-label={`Import "${it.name}"`}
            checked={selected.has(itemKey(it))}
            disabled={!canApply || applyMutation.isPending}
            onChange={() => toggleSelected(itemKey(it))}
          />
        ) : (
          <span aria-hidden="true">—</span>
        ),
    },
    { key: "name", header: "Name", cell: (it) => it.name, className: "data-table__primary" },
    { key: "level", header: "Kind", cell: (it) => (it.level === "project" ? "Project" : "Machine (discovered)") },
    { key: "confidence", header: "Confidence", cell: (it) => <ImportConfidencePill confidence={it.confidence} /> },
    {
      key: "conflicts",
      header: "Why",
      cell: (it) =>
        it.conflicts && it.conflicts.length > 0 ? (
          <ul className="org-import__conflicts">
            {it.conflicts.map((c, i) => (
              <li key={`${c.kind}-${i}`}>
                <ImportConflictPill kind={c.kind} />
                <span className="org-import__conflict-detail">{c.detail}</span>
              </li>
            ))}
          </ul>
        ) : it.level === "node" ? (
          <span className="org-import__note">
            Becomes a machine only when its agent enrolls — never created here.
          </span>
        ) : (
          "—"
        ),
    },
  ];

  return (
    <div className="org-import">
      <PageHeader
        title="Import existing infrastructure"
        description="Read a connected provider's existing organization and bring its structure into Comebye — nothing is created until you confirm."
      />

      {!canPropose && (
        <EmptyState
          title="You don't have access to import"
          description="Ask an admin or operator to import your existing infrastructure."
        />
      )}

      {canPropose && (
        <>
          <p className="form-note">
            This reads your provider account read-only and proposes a mapping — projects are created only after you
            select items below and apply.
          </p>

          <div className="org-import__source">
            <label className="form-field" htmlFor={sourceFieldId}>
              <span>Source</span>
              <select
                id={sourceFieldId}
                value={source}
                onChange={(e) => setSource(e.target.value)}
                disabled={credentialsPending}
              >
                <option value="" disabled>
                  {credentialsPending ? "Loading provider credentials…" : "Select a source"}
                </option>
                {credentials.length > 0 && (
                  <optgroup label="Provider credentials">
                    {credentials.map((c) => (
                      <option key={c.id} value={`${CREDENTIAL_PREFIX}${c.id}`}>
                        {c.label} ({PROVIDER_LABEL[c.kind]})
                      </option>
                    ))}
                  </optgroup>
                )}
                <optgroup label="Coming soon">
                  {DEFERRED_SOURCES.map((d) => (
                    <option key={d.provider} value={`${DEFERRED_PREFIX}${d.provider}`}>
                      {d.label}
                    </option>
                  ))}
                </optgroup>
              </select>
              {!credentialsPending && credentials.length === 0 && (
                <span className="form-field__hint">
                  No provider credentials yet — add one on the Provider Credentials page first, or try a "coming
                  soon" source to see its status.
                </span>
              )}
            </label>

            <Button
              variant="primary"
              onClick={handlePropose}
              disabled={!source || proposeMutation.isPending}
            >
              {proposeMutation.isPending ? "Reading…" : "Read organization"}
            </Button>
          </div>

          {proposeMutation.isPending && <LoadingState label="Reading your provider organization…" />}

          {proposeDeferred && proposeError && (
            <EmptyState title="Not available yet" description={proposeError.message} />
          )}

          {proposeMutation.isError && !proposeDeferred && (
            <FormError>
              {proposeError?.message ?? "Couldn't read that provider's organization. Try again in a moment."}
            </FormError>
          )}

          {proposal && items.length === 0 && !proposeMutation.isError && (
            <EmptyState
              title="Nothing found to import"
              description="This source has no resources for this account, or nothing it found could be mapped."
            />
          )}

          {proposal && items.length > 0 && (
            <>
              <DataTable columns={columns} rows={items} rowKey={itemKey} />

              {applyMutation.isSuccess && (
                <Banner tone="info" title="Import applied" action={<Link href="/projects">View projects</Link>}>
                  {formatApplyResult(applyMutation.data)}
                </Banner>
              )}

              {applyMutation.isError && (
                <FormError>
                  {applyMutation.error instanceof Error
                    ? applyMutation.error.message
                    : "Couldn't apply this import. Nothing further was created."}
                </FormError>
              )}

              {canApply ? (
                <div className="form-actions org-import__apply-actions">
                  <span className="form-field__hint">
                    {committableCount === 0
                      ? "Nothing here is safe to create automatically — the rest need a human to place them."
                      : `${selectedCommittableCount} of ${committableCount} project${committableCount === 1 ? "" : "s"} selected.`}
                  </span>
                  <Button
                    variant="primary"
                    onClick={handleApply}
                    disabled={selectedCommittableCount === 0 || applyMutation.isPending}
                  >
                    {applyMutation.isPending ? "Importing…" : `Import selected (${selectedCommittableCount})`}
                  </Button>
                </div>
              ) : (
                <p className="form-note">Ask an operator or admin to apply this import.</p>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
